import { type Cohort } from 'Cohorts';
import { stemmedWords } from 'Porter-stemmer';
import {
    type BrowseFilters,
    type BrowseStream,
    type FilterIndex,
    type FilterIndexEntry,
    type StreamFlag,
    type TopicGroup,
} from './BrowseCourses.types';
import { STATUS_FLAGS, STATUS_OPTIONS } from './constants';
import { getContentTopicNames, getProgressStatus, getSearchIndex, isElective } from './selectors';

export const EMPTY_FILTERS: BrowseFilters = { topics: [], progress: [], searchText: '', selectedStatus: 'any_status' };

const compareUpperCase = (a: string, b: string) => {
    const aCaps = a.toUpperCase();
    const bCaps = b.toUpperCase();
    if (aCaps < bCaps) return -1;
    if (bCaps < aCaps) return 1;
    return 0;
};

// Runs once per data load.
export function buildFilterIndex(
    streams: BrowseStream[],
    { localeCode, cohort, now }: { localeCode: string; cohort: Cohort | null; now: number },
): FilterIndex {
    const entries = new Map<string, FilterIndexEntry>();
    const topicsByStreamId = new Map<string, string[]>();
    const availableTopics = new Set<string>();
    const shownFlags = new Set<StreamFlag>();

    streams.forEach(stream => {
        const topics = getContentTopicNames(stream, localeCode);
        topics.forEach(topic => availableTopics.add(topic));
        topicsByStreamId.set(stream.id, topics);

        const flags: Record<StreamFlag, boolean> = {
            just_added: !!stream.just_added,
            just_updated: !!stream.just_updated,
            beta: !!stream.beta,
            coming_soon: !!stream.coming_soon,
            elective: isElective(stream, cohort),
        };
        (Object.keys(flags) as StreamFlag[]).forEach(flag => {
            if (flags[flag]) shownFlags.add(flag);
        });

        entries.set(stream.id, {
            topics: new Set(topics),
            progressStatus: getProgressStatus(stream, now),
            searchIndex: getSearchIndex(stream, localeCode),
            flags,
        });
    });

    // `any_status` and `available` always show. The rest show when some stream has the flag. When none
    // does, the page hides the whole status group.
    const visibleStatusOptions = STATUS_OPTIONS.filter(option => {
        const flag = STATUS_FLAGS[option];
        return !flag || shownFlags.has(flag);
    });

    return {
        entries,
        topicsByStreamId,
        availableTopics: [...availableTopics].sort(),
        visibleStatusOptions: shownFlags.size > 0 ? visibleStatusOptions : [],
    };
}

// Removes RegExp special characters, which can break the search.
export const sanitizeSearchText = (text: string) => text.replace(/[{}/[\]\\()$^*+.,:?|]/g, '');

function matchesSearch(entry: FilterIndexEntry, searchText: string) {
    return searchText.split(/\s+/).every(word => stemmedWords(word).every(term => entry.searchIndex.includes(term)));
}

function matchesFilters(
    stream: BrowseStream,
    entry: FilterIndexEntry,
    filters: BrowseFilters,
    canLaunch: (streamId: string) => boolean,
) {
    const { selectedStatus, topics, progress, searchText } = filters;

    if (selectedStatus === 'available' && !canLaunch(stream.id)) return false;
    const flag = STATUS_FLAGS[selectedStatus];
    if (flag && !entry.flags[flag]) return false;

    if (topics.length > 0 && !topics.some(topic => entry.topics.has(topic))) return false;
    if (progress.length > 0 && !progress.includes(entry.progressStatus)) return false;
    if (searchText && !matchesSearch(entry, searchText)) return false;

    return true;
}

function hasActiveFilters(filters: BrowseFilters) {
    return (
        filters.topics.length > 0 ||
        filters.progress.length > 0 ||
        !!filters.searchText ||
        filters.selectedStatus !== 'any_status'
    );
}

export function filterStreams(
    streams: BrowseStream[],
    index: FilterIndex,
    filters: BrowseFilters,
    canLaunch: (streamId: string) => boolean,
): BrowseStream[] {
    if (!hasActiveFilters(filters)) return streams;

    return streams.filter(stream => {
        const entry = index.entries.get(stream.id);
        return !!entry && matchesFilters(stream, entry, filters, canLaunch);
    });
}

export function sortByTitle(streams: BrowseStream[]): BrowseStream[] {
    return [...streams].sort((a, b) => compareUpperCase(a.title, b.title));
}

// A stream appears under each of its topics. Streams without a topic go under `otherTopicName`.
// When topic filters are set, only those topic groups show.
export function groupByTopic(
    streams: BrowseStream[],
    index: FilterIndex,
    { otherTopicName, topicFilters }: { otherTopicName: string; topicFilters: string[] },
): TopicGroup[] {
    const groups = new Map<string, BrowseStream[]>();

    sortByTitle(streams).forEach(stream => {
        const topics = index.topicsByStreamId.get(stream.id) ?? [];
        (topics.length > 0 ? topics : [otherTopicName]).forEach(topic => {
            groups.set(topic, [...(groups.get(topic) ?? []), stream]);
        });
    });

    return [...groups.keys()]
        .filter(topic => topicFilters.length === 0 || topicFilters.includes(topic))
        .sort(compareUpperCase)
        .map(topic => ({ topic, streams: groups.get(topic)! }));
}

// In topic mode, the limit counts cards across all groups. A course with 2 topics takes 2 cards.
export function limitTopicGroups(groups: TopicGroup[], limit: number): TopicGroup[] {
    let remaining = limit;
    const limited: TopicGroup[] = [];
    groups.forEach(group => {
        if (remaining <= 0) return;
        const streams = group.streams.slice(0, remaining);
        remaining -= streams.length;
        limited.push({ topic: group.topic, streams });
    });
    return limited;
}
